import { useMemo } from 'react';
import * as yup from 'yup';
import translation from 'i18n/translation.json';
import { GARegisterEvent, getStorageItem } from 'utils';
import type { UserInfo } from 'interfaces';

export const usePersonalInfoSchema = (data: UserInfo | null) => {
  const registration = data?.registration_type;

  const schema = useMemo(
    () =>
      yup.object({
        education: yup
          .string()
          .trim()
          .required(() => {
            GARegisterEvent({
              event: 'error_data',
              label: 'sign_up',
              stage: 'income_additional_inform',
              field_name: 'education',
              registration,
              error_text: 'error_frontend: Education field is required',
              UID: data?.user_uuid,
            });
            return translation.errors.requiredField;
          }),
        employment_industry: yup
          .string()
          .trim()
          .required(() => {
            GARegisterEvent({
              event: 'error_data',
              label: 'sign_up',
              stage: 'income_additional_inform',
              field_name: 'employment_industry',
              registration,
              error_text: 'error_frontend: Employment Industry field is required',
              UID: data?.user_uuid,
            });
            return translation.errors.requiredField;
          }),
        employment_type: yup
          .string()
          .trim()
          .required(() => {
            GARegisterEvent({
              event: 'error_data',
              label: 'sign_up',
              stage: 'income_additional_inform',
              field_name: 'employment_type',
              registration,
              error_text: 'error_frontend: Employment Type field is required',
              UID: data?.user_uuid,
            });
            return translation.errors.requiredField;
          }),
        monthly_net_income: yup
          .string()
          .test('non-zero', translation.errors.invalidAmount, value => {
            if (!value) return true;
            return Number(value) !== 0;
          })
          .required(() => {
            GARegisterEvent({
              event: 'error_data',
              label: 'sign_up',
              stage: 'income_additional_inform',
              field_name: 'monthly_net_income',
              registration,
              error_text: 'error_frontend: Monthly Net Income field is required',
              UID: data?.user_uuid,
            });
            return translation.errors.requiredField;
          }),
        has_active_loans: yup.string().required(() => {
          GARegisterEvent({
            event: 'error_data',
            label: 'sign_up',
            stage: 'income_additional_inform',
            field_name: 'has_active_loans',
            registration,
            error_text: 'error_frontend: Has active loans field is required',
            UID: data?.user_uuid,
          });
          return translation.errors.requiredField;
        }),
        monthly_expenses: yup
          .string()
          .test('non-zero', translation.errors.invalidAmount, value => {
            if (!value) return true;
            return Number(value) !== 0;
          })
          .required(() => {
            GARegisterEvent({
              event: 'error_data',
              label: 'sign_up',
              stage: 'income_additional_inform',
              field_name: 'monthly_expenses',
              registration,
              error_text: 'error_frontend: Monthly expenses field is required',
              UID: data?.user_uuid,
            });
            return translation.errors.requiredField;
          }),
        email: yup
          .string()
          .trim()
          .required(() => {
            GARegisterEvent({
              event: 'error_data',
              label: 'sign_up',
              stage: 'income_additional_inform',
              field_name: 'email',
              registration,
              error_text: 'error_frontend: Email field is required',
              UID: data?.user_uuid,
            });
            return translation.errors.requiredField;
          })
          .matches(
            /^[a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/,
            translation.errors.incorrectEmail,
          )
          .test('prefix', translation.errors.incorrectEmail, value => {
            if (!value) return false;
            GARegisterEvent({
              event: 'error_data',
              label: 'sign_up',
              stage: 'income_additional_inform',
              field_name: 'email',
              registration,
              error_text: 'error_frontend: Email field is invalid',
              UID: data?.user_uuid,
            });
            const prefix = value.split('@')[0];
            return /^[a-zA-Z0-9]([._-]?[a-zA-Z0-9]+)*$/.test(prefix) && !/[_.-]{2,}/.test(prefix);
          })
          .test('domain', translation.errors.incorrectEmail, value => {
            console.log(value);
            if (!value) return false;
            const domain = value.split('@')[1];
            if (!domain) return false;
            const parts = domain.split('.');
            if (parts.length < 2) return false;
            const lastPart = parts.pop();
            if (!lastPart || lastPart.length < 2) return false;
            return parts.every(part => /^[a-zA-Z0-9-]+$/.test(part));
          }),
      }),

    [data?.user_uuid, data?.phone_number, registration],
  );

  const defaultValue: yup.InferType<typeof schema> = {
    education: (getStorageItem('education') || data?.education) ?? '',
    employment_industry: (getStorageItem('employment_industry') || data?.employment_industry) ?? '',
    employment_type: (getStorageItem('employment_type') || data?.employment_type) ?? '',
    has_active_loans: (getStorageItem('has_active_loans') || data?.has_active_loans?.toString()) ?? '',
    email: data?.email ?? '',
    monthly_net_income: data?.monthly_net_income?.toString() ?? '',
    monthly_expenses: data?.monthly_expenses?.toString() ?? '',
  };

  return { schema, defaultValue };
};
